import React, { useState, useEffect } from 'react';
import { Tab, Button, Icon, Loader, Dimmer } from 'semantic-ui-react';
import SusuModal from '../common/SusuModal';
import { ChromePicker } from 'react-color';
import { useBackgrounds } from '../../hooks/useBackgrounds';
import ConfirmModal from '../common/ConfirmModal';
import './BackgroundModal.css';

const BACKGROUND_COLOR_PRESETS = [
  '#FFFFFF', // White
  '#F8F9FA', // Light gray
  '#E9ECEF', // Gray
  '#DEE2E6', // Medium gray
  '#ADB5BD', // Dark gray
  '#6C757D', // Darker gray
  '#343A40', // Very dark gray
  '#000000', // Black
  '#FFF3E0', // Light orange
  '#FFE0B2', // Peach
  '#FFCCBC', // Light coral
  '#F8BBD9', // Light pink
  '#E1BEE7', // Light purple
  '#C5CAE9', // Light blue
  '#BBDEFB', // Sky blue
  '#B2DFDB', // Light teal
  '#C8E6C9', // Light green
  '#DCEDC8', // Light lime
];

const GRADIENT_PRESETS = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
  'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
  'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
];

const BACKGROUND_PRESETS = {
  Studio: [
    {
      id: 'studio-white',
      name: 'Studio White',
      style: { backgroundColor: '#FFFFFF' },
    },
    {
      id: 'studio-gray',
      name: 'Studio Gray',
      style: { backgroundColor: '#F8F9FA' },
    },
    {
      id: 'studio-black',
      name: 'Studio Black',
      style: { backgroundColor: '#000000' },
    }
  ],
  Room: [
    {
      id: 'room-bedroom',
      name: 'Bedroom',
      style: { backgroundColor: '#FFF8E7' },
    },
    {
      id: 'room-closet',
      name: 'Closet',
      style: { backgroundColor: '#F0F4F8' },
    },
    {
      id: 'room-lounge',
      name: 'Lounge',
      style: { backgroundColor: '#FDF2F8' },
    }
  ],
  Outdoor: [
    {
      id: 'outdoor-sky',
      name: 'Sky Blue',
      style: { background: 'linear-gradient(180deg, #87CEEB 0%, #E0F6FF 100%)' },
    },
    {
      id: 'outdoor-nature',
      name: 'Nature Green',
      style: { background: 'linear-gradient(180deg, #90EE90 0%, #F0FFF0 100%)' },
    }
  ],
  Abstract: [
    {
      id: 'abstract-sunset',
      name: 'Sunset',
      style: { background: 'linear-gradient(135deg, #FF6B6B 0%, #FFE66D 100%)' },
    },
    {
      id: 'abstract-ocean',
      name: 'Ocean',
      style: { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
    }
  ]
};

const BackgroundModal = ({
  isOpen,
  onClose,
  currentBackground,
  onBackgroundChange,
  onImageUpload
}) => {
  // Use the new useBackgrounds hook with RTK Query
  const {
    background,
    userBackgrounds,
    publicBackgrounds,
    isLoading,
    isUploading,
    isDeleting,
    error,
    setBackgroundColor,
    setBackgroundGradient,
    setBackgroundImage,
    setBackgroundPreset,
    uploadBackground,
    deleteBackground,
    clearBackground,
    clearError
  } = useBackgrounds();

  const [activeTab, setActiveTab] = useState(0);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [customColor, setCustomColor] = useState(background.color || '#FFFFFF');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [backgroundToDelete, setBackgroundToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  // Update custom color when background changes
  useEffect(() => {
    if (background.type === 'color' && background.color) {
      setCustomColor(background.color);
    }
  }, [background]);

  const handleColorChange = (color) => {
    setCustomColor(color.hex);
    setBackgroundColor(color.hex);
  };

  const handlePresetColorSelect = (color) => {
    setCustomColor(color);
    setBackgroundColor(color);
  };

  const handleGradientSelect = (gradient) => {
    setBackgroundGradient(gradient);
  };

  const handlePresetSelect = (preset) => {
    setBackgroundPreset(preset);
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      await handleImageUploadToBackend(file);
    }
  };

  const handleImageDrop = async (file) => {
    if (file) {
      await handleImageUploadToBackend(file);
    }
  };

  const handleImageUploadToBackend = async (file) => {
    // Validate file
    if (!file.type.startsWith('image/')) {
      return;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return;
    }

    try {
      clearError();

      // Check if user is logged in
      const isLoggedIn = !!localStorage.getItem('token');

      if (!isLoggedIn) {
        // Save to localStorage for non-logged-in users
        const reader = new FileReader();
        reader.onloadend = () => {
          const imageData = reader.result;
          const guestBackgrounds = JSON.parse(localStorage.getItem('guestBackgrounds') || '[]');

          const newBackground = {
            id: Date.now(),
            image: { url: imageData },
            name: file.name,
            category: 'custom',
            createdAt: new Date().toISOString()
          };

          guestBackgrounds.unshift(newBackground);
          localStorage.setItem('guestBackgrounds', JSON.stringify(guestBackgrounds));

          // Set as active background
          setBackgroundImage(imageData);

          console.log('✅ Background saved to localStorage');
          onClose();
        };
        reader.readAsDataURL(file);
        return;
      }

      // Upload to backend using the hook (automatically sets as active background)
      await uploadBackground({
        file,
        name: file.name,
        description: 'Custom background',
        category: 'custom',
        isPublic: false
      });

      onClose();
    } catch (err) {
      console.error('Background upload failed:', err);
    }
  };

  const panes = [
    {
      menuItem: { key: 'colors', icon: 'paint brush', content: 'Colors' },
      render: () => (
        <Tab.Pane className="background-tab-pane colors-tab">
          <div className="color-section">
            <h4>Custom Color</h4>
            <div className="custom-color-controls">
              <Button
                className="color-picker-trigger"
                style={{ backgroundColor: customColor }}
                onClick={() => setShowColorPicker(!showColorPicker)}
              >
                <Icon name="tint" />
              </Button>
              {showColorPicker && (
                <div className="color-picker-popover">
                  <div
                    className="color-picker-cover"
                    onClick={() => setShowColorPicker(false)}
                  />
                  <ChromePicker
                    color={customColor}
                    onChange={handleColorChange}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="color-section">
            <h4>Preset Colors</h4>
            <div className="color-grid">
              {BACKGROUND_COLOR_PRESETS.map((color, index) => (
                <div
                  key={index}
                  className={`color-option ${background.type === 'color' && background.color === color ? 'selected' : ''}`}
                  style={{ backgroundColor: color }}
                  onClick={() => handlePresetColorSelect(color)}
                  title={color}
                />
              ))}
            </div>
          </div>

          <div className="color-section">
            <h4>Gradients</h4>
            <div className="gradient-grid">
              {GRADIENT_PRESETS.map((gradient, index) => (
                <div
                  key={index}
                  className={`gradient-option ${background.type === 'gradient' && background.gradient === gradient ? 'selected' : ''}`}
                  style={{ background: gradient }}
                  onClick={() => handleGradientSelect(gradient)}
                />
              ))}
            </div>
          </div>
        </Tab.Pane>
      )
    },
    {
      menuItem: { key: 'images', icon: 'image', content: 'Images' },
      render: () => (
        <Tab.Pane className="background-tab-pane images-tab">
          <div
            className={`upload-area ${isOperationInProgress ? 'loading' : ''}`}
            onClick={() => !isOperationInProgress && document.getElementById('background-image-upload-modal').click()}
            onDragOver={(e) => {
              e.preventDefault();
              if (!isOperationInProgress) {
                e.currentTarget.classList.add('drag-over');
              }
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove('drag-over');
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove('drag-over');
              if (!isOperationInProgress) {
                const files = e.dataTransfer.files;
                if (files.length > 0) {
                  handleImageDrop(files[0]);
                }
              }
            }}
          >
            <input
              type="file"
              id="background-image-upload-modal"
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: 'none' }}
              disabled={isOperationInProgress}
            />
            <Icon name="upload" size="large" />
            <h4>Upload Background Image</h4>
            <p>Click to select or drag and drop</p>
            <p className="upload-hint">
              Supported formats: JPG, PNG, GIF (max 5MB)
            </p>
            {error && (
              <div className="error-message">
                <Icon name="exclamation triangle" color="red" />
                <span>{error}</span>
                <Button
                  size="mini"
                  basic
                  onClick={clearError}
                  style={{ marginLeft: '8px' }}
                >
                  Dismiss
                </Button>
              </div>
            )}
          </div>

          {/* User's Uploaded Backgrounds */}
          {userBackgrounds && userBackgrounds.length > 0 && (
            <div className="user-backgrounds-section">
              <h4>Your Backgrounds ({(() => {
                const isLoggedIn = !!localStorage.getItem('token');
                const guestBgs = !isLoggedIn ? JSON.parse(localStorage.getItem('guestBackgrounds') || '[]') : [];
                const allBgs = isLoggedIn ? userBackgrounds : guestBgs;
                return allBgs.length;
              })()})</h4>
              <div className="backgrounds-grid">
                {(() => {
                  const isLoggedIn = !!localStorage.getItem('token');
                  const guestBgs = !isLoggedIn ? JSON.parse(localStorage.getItem('guestBackgrounds') || '[]') : [];
                  const allBgs = isLoggedIn ? userBackgrounds : guestBgs;

                  return allBgs.map((bg) => (
                    <div
                      key={bg.id}
                      className={`background-card ${background.imageUrl === bg.image?.url ? 'selected' : ''}`}
                      onClick={() => setBackgroundImage(bg.image?.url, null, bg.id)}
                    >
                      <div className="background-thumbnail">
                        <img
                          src={bg.thumbnail?.url || bg.image?.url}
                          alt={bg.name}
                        />
                      </div>
                      <div className="background-info">
                        <p className="background-name">{bg.name}</p>
                        {bg.lastUsed && (
                          <p className="background-meta">
                            Last used: {new Date(bg.lastUsed).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <div
                        className="delete-background-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setBackgroundToDelete(bg);
                          setDeleteConfirmOpen(true);
                        }}
                        title="Delete background"
                      >
                        <Icon name="close" color="red" />
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}

          {/* Public Backgrounds */}
          {publicBackgrounds && publicBackgrounds.length > 0 && (
            <div className="public-backgrounds-section">
              <h4>Public Backgrounds</h4>
              <div className="backgrounds-grid">
                {publicBackgrounds.slice(0, 6).map((bg) => (
                  <div
                    key={bg.id}
                    className={`background-card ${background.imageUrl === bg.image?.url ? 'selected' : ''}`}
                    onClick={() => setBackgroundImage(bg.image?.url, null, bg.id)}
                  >
                    <div className="background-thumbnail">
                      <img
                        src={bg.thumbnail?.url || bg.image?.url}
                        alt={bg.name}
                      />
                    </div>
                    <div className="background-info">
                      <p className="background-name">{bg.name}</p>
                      <p className="background-meta">
                        <Icon name="heart" /> {bg.usageCount || 0} uses
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Tab.Pane>
      )
    },
    {
      menuItem: { key: 'presets', icon: 'grid layout', content: 'Presets' },
      render: () => (
        <Tab.Pane className="background-tab-pane presets-tab">
          {Object.entries(BACKGROUND_PRESETS).map(([category, presets]) => (
            <div key={category} className="preset-category">
              <h4>{category} Backgrounds</h4>
              <div className="preset-grid">
                {presets.map((preset) => (
                  <div
                    key={preset.id}
                    className={`preset-card ${background.type === 'preset' && background.preset?.id === preset.id ? 'selected' : ''}`}
                    onClick={() => handlePresetSelect(preset)}
                    title={preset.name}
                  >
                    <div
                      className="preset-preview"
                      style={preset.style}
                    >
                      {preset.name}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </Tab.Pane>
      )
    }
  ];

  const handleConfirmDelete = async () => {
    if (backgroundToDelete) {
      try {
        setDeleteError(null);

        const isLoggedIn = !!localStorage.getItem('token');

        if (!isLoggedIn) {
          // Delete from localStorage
          const guestBackgrounds = JSON.parse(localStorage.getItem('guestBackgrounds') || '[]');
          const updated = guestBackgrounds.filter(b => b.id !== backgroundToDelete.id);
          localStorage.setItem('guestBackgrounds', JSON.stringify(updated));
          window.location.reload();
        } else {
          // Delete from backend
          await deleteBackground(backgroundToDelete.id);
          setDeleteConfirmOpen(false);
          setBackgroundToDelete(null);
        }
      } catch (error) {
        console.error('Delete error:', error);
        const errorMessage = error?.data?.message ||
          error?.message ||
          'Failed to delete background';
        setDeleteError(errorMessage);
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setBackgroundToDelete(null);
    setDeleteError(null);
  };

  // Determine if any operation is in progress
  const isOperationInProgress = isUploading || isDeleting;

  return (
    <>
      <SusuModal
        open={isOpen}
        onClose={onClose}
        className="background-modal"
        size="large"
        closeIcon
      >
        <Dimmer active={isOperationInProgress} inverted>
          <Loader size="large">Processing...</Loader>
        </Dimmer>

        <SusuModal.Header>
          {/* <Icon name="image outline" /> */}
          Canvas Customization
        </SusuModal.Header>

        <SusuModal.Content>
          <div className="background-modal-content">
            <Tab
              panes={panes}
              activeIndex={activeTab}
              onTabChange={(e, { activeIndex }) => setActiveTab(activeIndex)}
              className="background-tabs"
            />
          </div>
        </SusuModal.Content>
      </SusuModal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={handleCancelDelete}
        onConfirm={!deleteError ? handleConfirmDelete : undefined}
        title="Delete Background"
        titleIconName="trash"
        titleIconColor="red"
        confirmText="Delete"
        cancelText={deleteError ? 'Close' : 'Cancel'}
        variant="danger"
        isLoading={isDeleting}
        loadingText="Deleting..."
        confirmDisabled={isDeleting}
        closeOnOverlay={!isDeleting}
      >
        {deleteError ? (
          <div className="confirm-modal-error">
            <Icon name="exclamation triangle" style={{ margin: 0 }} />
            <span>{deleteError}</span>
          </div>
        ) : (
          <p>Are you sure you want to delete this background?</p>
        )}
        {backgroundToDelete && (
          <div style={{ textAlign: 'center', marginTop: '10px' }}>
            <img
              src={backgroundToDelete.thumbnail?.url || backgroundToDelete.image?.url}
              alt="Preview"
              style={{
                maxWidth: '200px',
                maxHeight: '150px',
                borderRadius: '8px',
                border: '2px solid #e0e0e0'
              }}
            />
          </div>
        )}
      </ConfirmModal>
    </>
  );
};

export default BackgroundModal;